import {
    Box,
    FormControlLabel,
    Checkbox,
    Typography,
    Stack,
    Tooltip,
    IconButton,
} from "@mui/material";
import { JsonSchemaEditor } from "components/JsonSchema";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import { selectResultTypeOptions, ResultType } from "models/ClassifierModel";
import { useCallback, useEffect, useState } from "react";
import ClassifierService from "services/ClassifierService";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import { delay } from "utils/helpers";
import { HelpOutlined } from "@mui/icons-material";
import { AcxMessageBox } from "components/UI/AcxMessageBox";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";

interface ExtendedMetadataDialogProps {
    open: boolean;
    defaultValue?: ExtendedMetadataClassifier;
    existingMetadata: ExtendedMetadataClassifier[];
    onSave: (extendedMetadata: ExtendedMetadataClassifier) => void;
    onClose: () => void;
}

const defaultExtendedMetadataValue: ExtendedMetadataClassifier = {
    id: "",
    name: "",
    isIndexed: false,
    isGroupable: false,
    isPublished: true,
    isActive: true,
    resultType: ResultType.StringType,
    jsonSchema: undefined,
};

const classifierService = new ClassifierService();

export const ExtendedMetadataDialog = observer(
    (props: ExtendedMetadataDialogProps) => {
        const [value, setValue] = useState(
            props.defaultValue ?? defaultExtendedMetadataValue,
        );
        const [fieldError, setFieldError] = useState("");
        const [isSaving, setIsSaving] = useState(false);

        const authStore = useStore(AuthStore);
        const canEditDataSources = authStore.canUserEdit("Data Sources");
        const canViewMetadataManagement = authStore.canUserView(
            "Metadata Management",
        );
        const isViewOnly = canViewMetadataManagement && !canEditDataSources;

        useEffect(() => {
            setValue(props.defaultValue ?? defaultExtendedMetadataValue);
            setFieldError("");
            setIsSaving(false);
        }, [props.defaultValue]);

        const showGroupableWarning =
            props.defaultValue?.isGroupable && !value.isGroupable;

        const update = useCallback(
            <T extends keyof ExtendedMetadataClassifier>(
                key: T,
                value: ExtendedMetadataClassifier[T],
            ) => {
                setValue((old) => {
                    var isIndexed = old.isIndexed;
                    var isGroupable = old.isGroupable;

                    // Reset isIndexed & isGroupable to false when selecting json result type
                    if (key === "resultType" && value === ResultType.JsonType) {
                        isIndexed = false;
                        isGroupable = false;
                    }

                    return { ...old, isIndexed, isGroupable, [key]: value };
                });
            },
            [],
        );

        const isEditing = !!props.defaultValue;
        const trimmedName = value.name.trim();

        const isJsonResultType = value.resultType === ResultType.JsonType;
        const isIndexable = !isJsonResultType;
        // const isGroupable = !isJsonResultType;

        const handleSave = async () => {
            if (!trimmedName) return;

            const isDuplicate = props.existingMetadata.some(
                (field) =>
                    field.name === trimmedName &&
                    field.id !== props.defaultValue?.id,
            );

            if (isDuplicate) {
                setFieldError(`"${trimmedName}" already exists.`);
                return;
            }

            setIsSaving(true);
            try {
                const savedField = isEditing
                    ? await classifierService.updateExtendedMetadataClassifier({
                          ...props.defaultValue!,
                          ...value,
                          name: props.defaultValue!.name,
                      })
                    : await classifierService.createExtendedMetadataClassifier({
                          name: trimmedName,
                          description: value.description,
                          resultType: value.resultType,
                          jsonSchema: value.jsonSchema,
                          isIndexed: value.isIndexed,
                          isGroupable: value.isGroupable,
                      });

                props.onSave(savedField);
                props.onClose();
            } catch (err: any) {
                const errorMessage = err?.errorMessage || "";
                if (errorMessage.includes("groupable limit")) {
                    setFieldError(errorMessage);
                    update("isGroupable", false);
                } else if (isEditing) {
                    setFieldError("Failed to update field.");
                } else {
                    setFieldError("Failed to add field.");
                }
            }
            setIsSaving(false);
        };

        return (
            <AcxDialog
                title={
                    isEditing ? "Edit Metadata Field" : "Create Metadata Field"
                }
                isOpen={props.open}
                onClose={async () => {
                    props.onClose();

                    await delay(150);
                    setValue(defaultExtendedMetadataValue);
                    setFieldError("");
                    setIsSaving(false);
                }}
                maxWidth="xl"
                dialogContentSx={{ minWidth: "400px" }}
                dialogContentChildren={
                    <Stack rowGap={2}>
                        <AcxMainTextField
                            id="add-field-name"
                            fullWidth
                            labelText="Custom Extended Metadata Field Name"
                            placeholderText="Enter field name..."
                            value={value.name}
                            isDisabled={isEditing}
                            onChange={(e) => {
                                update("name", e.target.value);
                                setFieldError("");
                            }}
                        />
                        <AcxMainTextField
                            id="add-field-description"
                            fullWidth
                            multiline
                            rows={3}
                            labelText="Description (optional)"
                            placeholderText="Enter a description..."
                            value={value.description ?? ""}
                            isDisabled={isViewOnly}
                            onChange={(e) => {
                                update("description", e.target.value);
                                setFieldError("");
                            }}
                        />
                        <Box>
                            <AcxSelectSingle
                                id="result-type"
                                fullWidth
                                options={selectResultTypeOptions}
                                defaultValue={selectResultTypeOptions.find(
                                    (o) => o.value === value.resultType,
                                )}
                                onChange={(option) =>
                                    update("resultType", option.value)
                                }
                                isDisabled={isEditing}
                                valueField="value"
                                labelField="label"
                                inputLabel="Extended Metadata Value Type"
                            />
                        </Box>
                        {isIndexable && (
                            <Stack direction="row" spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={value.isIndexed}
                                            disabled={isViewOnly}
                                            onChange={(e) => {
                                                const checked =
                                                    e.target.checked;
                                                update("isIndexed", checked);
                                                if (!checked) {
                                                    update(
                                                        "isGroupable",
                                                        false,
                                                    );
                                                }
                                            }}
                                        />
                                    }
                                    label="Reportable"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={value.isGroupable}
                                            disabled={
                                                !value.isIndexed ||
                                                isViewOnly
                                            }
                                            onChange={(e) => {
                                                update(
                                                    "isGroupable",
                                                    e.target.checked,
                                                );
                                            }}
                                        />
                                    }
                                    label={
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                        >
                                            <Typography>Groupable</Typography>
                                            <Tooltip title="Enables grouping in Signals reports and filters. Must be Reportable and fall within your org's groupable limit.">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    aria-label="Groupable info"
                                                >
                                                    <HelpOutlined fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    }
                                />
                            </Stack>
                        )}
                        {props.defaultValue?.isActive === false && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={value.isActive !== false}
                                        onChange={(e) => {
                                            update(
                                                "isActive",
                                                e.target.checked,
                                            );
                                        }}
                                    />
                                }
                                label="Active"
                                sx={{ ml: 0 }}
                            />
                        )}
                        {value.resultType === ResultType.JsonType && (
                            <JsonSchemaEditor
                                defaultValue={props.defaultValue?.jsonSchema}
                                onChange={(schema) =>
                                    update("jsonSchema", schema)
                                }
                                addOnly={isEditing}
                            />
                        )}
                        {fieldError && (
                            <Typography
                                sx={{ maxWidth: "352px" }}
                                color="error"
                                fontSize={13}
                            >
                                {fieldError}
                            </Typography>
                        )}
                        {showGroupableWarning && (
                            <AcxMessageBox
                                variant="warning"
                                sx={{ maxWidth: "352px" }}
                                childrenSx={{ fontWeight: 400 }}
                                childComponent="div"
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: "inherit",
                                        mb: 1,
                                    }}
                                >
                                    This change may break existing reports and
                                    filters.
                                </Typography>
                                <Typography sx={{ color: "inherit", mb: 1 }}>
                                    Marking this field as
                                    <strong> Not groupable </strong>
                                    will impact associated Signals reports and
                                    may break saved filters using the
                                    <strong> “is not one of” </strong>
                                    operator when they are next edited.
                                </Typography>
                                <Typography sx={{ color: "inherit" }}>
                                    Do you want to proceed?
                                </Typography>
                            </AcxMessageBox>
                        )}
                    </Stack>
                }
            >
                <AcxButton
                    color="primary"
                    disabled={
                        !trimmedName ||
                        isSaving ||
                        isViewOnly
                    }
                    loading={isSaving}
                    onClick={handleSave}
                >
                    {isEditing ? "Save Changes" : "Save"}
                </AcxButton>
            </AcxDialog>
        );
    },
);
